import { AIR } from '@vm/shared';
import { family, M } from '../palette';
import { blocks, bricks, MapBuilder, patches, planks, solid, stripes, symPoint, symYaw, yawToward } from './builder';
import { emptyMeta, type MapDef, type MapMeta } from './types';

const S = 640;
const G = 32;
const SYM = 'mirrorX' as const;

/** Pit floor (first air) in the central excavation. */
const PIT = 14;

/** Steel-frame building under construction: concrete slabs on steel columns, open stairs, rebar. */
function steelFrame(b: MapBuilder, x0: number, z0: number, x1: number, z1: number, floors: number): void {
  const col = M('steel_dark_1');
  const beam = M('steel_dark_2');
  const slab = blocks(family('concrete'), 5, 3);
  const FH = 32;
  b.box(x0 - 4, G - 1, z0 - 4, x1 + 4, G, z1 + 4, M('concrete_4'));
  const xs = [x0, Math.floor((2 * x0 + x1) / 3) - 1, Math.floor((x0 + 2 * x1) / 3) - 2, x1 - 3];
  const zs = [z0, Math.floor((2 * z0 + z1) / 3) - 1, Math.floor((z0 + 2 * z1) / 3) - 2, z1 - 3];
  for (let f = 0; f < floors; f++) {
    const fy = G + f * FH;
    const top = f === floors - 1;
    // Columns (the top floor's stop short: the frame is still going up).
    for (const x of xs) for (const z of zs) b.box(x, fy, z, x + 3, fy + (top ? 22 : FH - 2), z + 3, col);
    if (top) continue;
    // Slab above this floor, with beams along the column lines underneath.
    const sy = fy + FH - 2;
    const partial = f === floors - 2;
    const sx1 = partial ? xs[2] + 3 : x1;
    b.box(x0, sy, z0, sx1, sy + 2, z1, slab);
    for (const z of zs) b.box(x0, sy - 2, z, sx1, sy, z + 3, beam);
    for (const x of xs) if (x < sx1) b.box(x, sy - 2, z0, x + 3, sy, z1, beam);
    // Open steel stairs in two lanes along the north side (scissor layout): even floors climb east
    // in lane A, odd floors climb west in lane B, so each flight starts where the last one ended.
    // Each flight has a 0.8 m landing at its foot, runs under an opening cut through the slab (and
    // its beams) above, and its top step sits flush with the floor it serves.
    const W = 12;
    const east = f % 2 === 0;
    const lz = east ? z0 + 6 : z0 + 20;
    const xa = x0 + 12;
    const o0 = east ? xa + 4 : xa + 2;
    const o1 = east ? xa + 30 : xa + 28;
    b.clear(o0, sy - 2, lz, o1, sy + 2, lz + W);
    b.stairs(east ? xa : xa + FH - 1, fy, lz, east ? 'e' : 'w', W, FH, M('metal_2'), 3);
    // Guard rails along both long sides of the opening, on the floor above.
    for (const rz of [lz - 1, lz + W]) b.box(o0, sy + 2, rz, o1, sy + 11, rz + 1, M('paint_yellow'));
  }
  // Rebar sticking up from the tops of the columns and the partial slab edge.
  const rebar = M('rust');
  const topY = G + (floors - 1) * FH + 22;
  for (const x of xs) for (const z of zs) for (const [dx, dz] of [[0, 0], [2, 2]]) b.box(x + dx, topY, z + dz, x + dx + 1, topY + 8, z + dz + 1, rebar);
  const edgeY = G + (floors - 1) * FH;
  for (let z = z0 + 2; z < z1 - 2; z += 6) b.box(xs[2] + 2, edgeY, z, xs[2] + 3, edgeY + 6, z + 1, rebar);
  // Half-built block walls on the ground floor (waist-high cover).
  const wall = bricks([M('brick_gray')], 0);
  b.box(x0, G, z0, x1, G + 12, z0 + 3, wall);
  b.box(x0, G, z0, x0 + 3, G + 16, z1, wall);
  b.clear(x0, G, Math.floor((z0 + z1) / 2) - 8, x0 + 3, G + 16, Math.floor((z0 + z1) / 2) + 8);
}

/** Tube-and-plank scaffold along a face, one deck per floor. */
function scaffold(b: MapBuilder, x0: number, z0: number, x1: number, z1: number, decks: number[]): void {
  const pole = M('scaffold');
  const deck = planks([M('planks_3')], 0);
  const alongX = x1 - x0 > z1 - z0;
  const top = decks[decks.length - 1] + 12;
  const posts: [number, number][] = [];
  if (alongX) {
    for (let x = x0; x <= x1 - 2; x += 30) posts.push([x, z0], [x, z1 - 2]);
    posts.push([x1 - 2, z0], [x1 - 2, z1 - 2]);
  } else {
    for (let z = z0; z <= z1 - 2; z += 30) posts.push([x0, z], [x1 - 2, z]);
    posts.push([x0, z1 - 2], [x1 - 2, z1 - 2]);
  }
  for (const [x, z] of posts) b.box(x, G, z, x + 2, top, z + 2, pole);
  for (const y of decks) {
    b.box(x0, y - 2, z0, x1, y, z1, deck);
    // Guard rails on the outer side.
    if (alongX) b.box(x0, y + 9, z1 - 1, x1, y + 10, z1, pole);
    else b.box(x1 - 1, y + 9, z0, x1, y + 10, z1, pole);
  }
  // Diagonal bracing on the outer face, between neighboring posts.
  for (let i = 0; i + 1 < posts.length; i += 2) {
    const [ax, az] = posts[i + 1];
    if (alongX && ax + 30 <= x1) b.line(ax, G + 2, az + 1, ax + 28, decks[0] - 3, az + 1, pole);
    else if (!alongX && az + 30 <= z1) b.line(ax + 1, G + 2, az, ax + 1, decks[0] - 3, az + 28, pole);
  }
}

/** Portable site office: a white box with windows and a door on one long side. */
function siteCabin(b: MapBuilder, x: number, y: number, z: number, doorSide: 'n' | 's'): void {
  const L = 60;
  const W = 26;
  const H = 26;
  b.box(x, y, z, x + L, y + H, z + W, solid([M('paint_white')]));
  b.clear(x + 1, y + 1, z + 1, x + L - 1, y + H - 1, z + W - 1);
  b.box(x + 1, y, z + 1, x + L - 1, y + 1, z + W - 1, M('planks_dark_1'));
  b.box(x - 1, y + H - 1, z - 1, x + L + 1, y + H, z + W + 1, M('steel_dark_1'));
  const dz = doorSide === 'n' ? z : z + W - 1;
  const wz = doorSide === 'n' ? z + W - 1 : z;
  for (const wx of [x + 6, x + 22, x + 40]) {
    b.box(wx, y + 10, wz, wx + 12, y + 19, wz + 1, M('glass_tint'));
    b.box(wx, y + 10, dz, wx + 12, y + 19, dz + 1, M('glass_tint'));
  }
  b.clear(x + 24, y + 1, dz, x + 34, y + 22, dz + 1);
}

/** Pallet of bricks or blocks. */
function pallet(b: MapBuilder, x: number, z: number, load: number, h = 8): void {
  b.box(x, G, z, x + 12, G + 2, z + 12, M('planks_4'));
  b.box(x + 1, G + 2, z + 1, x + 11, G + 2 + h, z + 11, load);
}

/** Traffic cone. */
function cone(b: MapBuilder, x: number, z: number): void {
  b.box(x - 1, G, z - 1, x + 2, G + 1, z + 2, M('paint_black'));
  b.box(x, G + 1, z, x + 1, G + 6, z + 1, M('paint_orange'));
  b.box(x, G + 3, z, x + 1, G + 4, z + 1, M('paint_white'));
}

/** Striped road barrier. */
function barrier(b: MapBuilder, x: number, z: number, alongX: boolean, len = 20): void {
  const s = stripes(M('paint_orange'), M('paint_white'), 4, alongX ? 'x' : 'z');
  if (alongX) {
    b.box(x, G + 5, z, x + len, G + 9, z + 1, s);
    b.box(x + 1, G, z, x + 2, G + 5, z + 1, M('steel_dark_1'));
    b.box(x + len - 2, G, z, x + len - 1, G + 5, z + 1, M('steel_dark_1'));
  } else {
    b.box(x, G + 5, z, x + 1, G + 9, z + len, s);
    b.box(x, G, z + 1, x + 1, G + 5, z + 2, M('steel_dark_1'));
    b.box(x, G, z + len - 2, x + 1, G + 5, z + len - 1, M('steel_dark_1'));
  }
}

/** Cable spool lying on its rim (axis along z). */
function spool(b: MapBuilder, x: number, z: number, r = 9): void {
  for (const dz of [0, 9]) {
    for (let dy = -r; dy <= r; dy++)
      for (let dx = -r; dx <= r; dx++) if (dx * dx + dy * dy <= r * r) b.set(x + dx, G + r + dy, z + dz, M('planks_2'));
  }
  for (let dy = -5; dy <= 5; dy++)
    for (let dx = -5; dx <= 5; dx++) if (dx * dx + dy * dy <= 25) b.box(x + dx, G + r + dy, z + 1, x + dx + 1, G + r + dy + 1, z + 9, M('paint_black'));
}

/** Work light on a tall post. */
function workLight(b: MapBuilder, x: number, z: number): void {
  b.box(x, G, z, x + 2, G + 44, z + 2, M('steel_dark_1'));
  b.box(x - 3, G + 44, z - 1, x + 5, G + 48, z + 3, M('steel_dark_2'));
  b.box(x - 2, G + 44, z + 3, x + 4, G + 47, z + 4, M('neon_yellow'));
}

/** Half of the site (west, team 0); the east half is its mirror image. */
function westHalf(b: MapBuilder): void {
  // Spawn yard: concrete pad walled in by stacked containers and the site offices.
  b.box(8, G - 1, 236, 96, G, 404, blocks(family('concrete'), 5, 5));
  b.container(96, G, 236, false, M('paint_red'), false);
  b.container(96, G + 26, 236, false, M('rust'), false);
  b.container(96, G, 344, false, M('paint_blue'), false);
  b.container(96, G + 26, 344, false, M('paint_green'), false);
  // Chicane inside the gap so the center cannot see straight into the yard.
  b.box(60, G, 302, 66, G + 22, 338, blocks([M('concrete_1')], 3, 1));
  b.crate(48, G, 300);
  b.crate(48, G, 328, 8);
  siteCabin(b, 14, G, 200, 's');
  siteCabin(b, 14, G + 26, 200, 's');
  // External stairs up to a balcony in front of the top office's door.
  b.stairs(100, G, 228, 'w', 10, 26, M('metal_2'), 3);
  b.box(14, G + 23, 226, 76, G + 26, 238, M('metal_2'));
  b.box(14, G + 26, 237, 72, G + 35, 238, M('paint_yellow'));
  b.box(14, G + 26, 226, 15, G + 35, 238, M('paint_yellow'));
  for (const x of [16, 44, 72]) b.box(x, G, 234, x + 2, G + 23, 236, M('steel_dark_1'));
  b.barrel(20, G, 244, M('team_red'));
  b.barrel(28, G, 248, M('paint_yellow'));
  // Team banner on the perimeter hoarding.
  b.box(4, G + 4, 290, 6, G + 22, 350, M('team_red'));

  // Materials yard (point A): pipes, piles, pallets, spools.
  b.box(118, G - 1, 262, 236, G, 378, solid([M('stone_4')]));
  b.pipe(126, G, 266, true, 44, 12);
  b.pipe(126, G, 350, true, 44, 12);
  b.pipe(210, G, 282, false, 30, 12);
  b.cone(214, G, 356, 16, 13, M('sand_2'));
  b.cone(146, G, 318, 10, 8, M('cobble_2'));
  pallet(b, 124, 298, M('brick_red_1'));
  pallet(b, 124, 330, M('brick_red_2'), 6);
  pallet(b, 186, 296, M('concrete_3'), 10);
  spool(b, 196, 332);
  for (let i = 0; i < 6; i++) b.box(170 + (i % 3) * 2, G + (i >= 3 ? 1 : 0), 250, 171 + (i % 3) * 2, G + 1 + (i >= 3 ? 1 : 0), 258 + 30, M('rust'));
  workLight(b, 116, 256);

  // North-west: steel-frame tower with a scaffold on its south face.
  steelFrame(b, 116, 56, 226, 166, 3);
  scaffold(b, 116, 168, 226, 184, [G + 32, G + 64]);
  // Stair tower at the scaffold's east end: a flight climbing north from the haul road to a landing
  // that joins the first deck.
  b.stairs(228, G, 215, 'n', 12, 32, M('metal_2'), 3);
  b.line(240, G + 10, 214, 240, G + 41, 184, M('paint_yellow'));
  b.box(226, G + 30, 166, 244, G + 32, 184, planks([M('planks_3')], 0));
  for (const z of [166, 182]) b.box(242, G, z, 244, G + 30, z + 2, M('scaffold'));
  b.box(242, G + 32, 166, 244, G + 41, 184, M('paint_yellow'));
  // Formwork and short concrete walls between the frame and the crane.
  for (const [x, z, len] of [[252, 70, 40], [252, 130, 30]] as const) {
    b.box(x, G, z, x + 4, G + 14, z + len, M('concrete_1'));
    for (let zz = z + 2; zz < z + len; zz += 6) b.box(x + 1, G + 14, zz, x + 2, G + 22, zz + 1, M('rust'));
  }
  b.box(250, G, 118, 262, G + 8, 124, M('planks_2'));

  // South-west: container stacks with lanes; one open stack has stairs to its roof.
  b.container(112, G, 452, true, M('paint_orange'));
  b.container(112, G + 26, 452, true, M('paint_blue'), false);
  b.container(186, G, 452, true, M('paint_green'), false);
  b.container(126, G, 530, true, M('rust'));
  b.container(196, G, 522, false, M('paint_red'));
  b.container(236, G, 470, false, M('paint_teal'), false);
  b.stairs(174, G, 478, 'n', 12, 26, M('metal_2'), 3);
  b.box(174, G + 25, 452, 186, G + 26, 454, M('metal_2'));
  b.crate(160, G, 500);
  b.crate(170, G, 500, 8);
  b.crate(164, G + 10, 501, 8);
  barrier(b, 250, 430, true, 40);
  for (const [x, z] of [[256, 424], [276, 424], [118, 420], [138, 420]]) cone(b, x, z);
  workLight(b, 108, 600);
  workLight(b, 108, 40);

  // Explosives: beside the crane footing (enough to take out the mast legs), inside the steel
  // frame by a column, and at the back of the container lanes.
  b.tntCrate(286, G, 124);
  b.tntCrate(198, G, 136, 7);
  b.tntBarrel(220, G, 572);

  // Scaffold-wrapped brick wall flanking the pit ramp.
  b.box(200, G, 246, 206, G + 34, 284, bricks([M('brick_red_1')], 0));
  b.box(200, G + 34, 246, 206, G + 40, 266, bricks([M('brick_red_1')], 0));
  scaffold(b, 206, 246, 222, 284, [G + 20]);
  b.box(200, G, 356, 206, G + 26, 394, bricks([M('brick_red_2')], 0));
  pallet(b, 212, 372, M('brick_red_1'), 10);

  // Excavation (west half; the mirror completes it): pit, piling walls, ramp and north steps.
  const pile = M('rust');
  b.clear(240, PIT, 250, 320, G, 390);
  b.box(240, PIT - 1, 250, 320, PIT, 390, M('dirt_2'));
  b.box(238, PIT - 1, 248, 320, G + 2, 250, pile);
  b.box(238, PIT - 1, 390, 320, G + 2, 392, pile);
  b.box(238, PIT - 1, 250, 240, G + 2, 390, pile);
  // Ramp down from the west (6 m wide), cut through the piling.
  for (let x = 196; x < 240; x++) {
    const top = G - Math.floor(((x - 196) * (G - PIT)) / 44);
    b.clear(x, top, 292, x + 1, G + 2, 348);
    b.box(x, top - 1, 292, x + 1, top, 348, M('dirt_1'));
  }
  b.box(196, G - 1, 290, 240, G + 3, 292, M('steel_dark_2'));
  b.box(196, G - 1, 348, 240, G + 3, 350, M('steel_dark_2'));
  // Steps down from the north rim.
  b.clear(296, PIT, 230, 320, G + 2, 250);
  b.stairs(296, PIT, 250, 'n', 24, G - PIT, M('concrete_2'));
  // Cover inside the pit: rebar cages and formwork blocks.
  b.box(262, PIT, 268, 282, PIT + 12, 280, M('planks_2'));
  b.box(282, PIT, 356, 294, PIT + 16, 372, blocks([M('concrete_2')], 3, 0));
  for (let x = 250; x < 272; x += 4) b.box(x, PIT, 330, x + 1, PIT + 14, 331, pile);
  b.box(250, PIT + 13, 330, 272, PIT + 14, 331, pile);
}

/** Tower crane: lattice mast on a footing, jib over the pit with a load hanging on its cable. */
function crane(b: MapBuilder): void {
  const yel = M('scaffold');
  const cx = 320;
  const cz = 150;
  b.box(cx - 22, G, cz - 22, cx + 22, G + 10, cz + 22, blocks([M('concrete_1')], 3, 0));
  const base = G + 10;
  const head = 186;
  b.lattice(cx - 10, base, cz - 10, cx + 10, head, cz + 10, 'y', yel, 2, 18);
  // Slewing platform, cab and the tower head.
  b.box(cx - 13, head, cz - 13, cx + 13, head + 5, cz + 13, M('steel_dark_1'));
  b.box(cx + 10, head - 14, cz - 2, cx + 24, head, cz + 12, M('paint_white'));
  b.box(cx + 11, head - 11, cz + 12, cx + 23, head - 3, cz + 13, M('glass_tint'));
  b.box(cx + 24, head - 11, cz, cx + 25, head - 3, cz + 11, M('glass_tint'));
  const apex = head + 30;
  for (const [dx, dz] of [[-9, -9], [8, -9], [-9, 8], [8, 8]]) b.line(cx + dx, head + 5, cz + dz, cx, apex, cz, yel, 2);
  b.box(cx - 2, apex, cz - 2, cx + 2, apex + 3, cz + 2, yel);
  // Jib (south, over the pit) and counter-jib (north) with its counterweights.
  const jy = head + 5;
  b.lattice(cx - 8, jy, cz + 13, cx + 8, jy + 12, 478, 'z', yel, 2, 16);
  b.lattice(cx - 8, jy, 52, cx + 8, jy + 8, cz - 13, 'z', yel, 2, 16);
  b.box(cx - 10, jy - 16, 56, cx + 10, jy, 86, blocks([M('concrete_2')], 3, 0));
  // Pendant ties from the apex to both jibs.
  b.line(cx, apex, cz, cx, jy + 12, 400, M('steel_dark_2'));
  b.line(cx, apex, cz, cx, jy + 8, 70, M('steel_dark_2'));
  // Trolley, cable, hook block and the hanging container over the pit.
  const tz = 320;
  b.box(cx - 6, jy - 3, tz - 6, cx + 6, jy, tz + 6, M('steel_dark_1'));
  const loadTop = 128;
  b.box(cx - 1, loadTop + 12, tz - 1, cx + 1, jy - 3, tz + 1, M('steel_dark_2'));
  b.box(cx - 4, loadTop + 6, tz - 3, cx + 4, loadTop + 12, tz + 3, M('paint_yellow'));
  for (const [dx, dz] of [[-28, -10], [27, -10], [-28, 9], [27, 9]]) b.line(cx, loadTop + 6, tz, cx + dx, loadTop, tz + dz, M('steel_dark_2'));
  b.container(cx - 30, loadTop - 26, tz - 12, true, M('paint_orange'), false);
}

/** Tracked excavator at the south rim, its arm reaching into the pit. */
function excavator(b: MapBuilder): void {
  const x = 320;
  const z = 430;
  const yel = M('paint_yellow');
  for (const dx of [-22, 12]) {
    b.box(x + dx, G, z - 24, x + dx + 10, G + 10, z + 24, M('rubber'));
    b.box(x + dx + 2, G + 2, z - 26, x + dx + 8, G + 8, z + 26, M('rubber'));
  }
  b.box(x - 12, G + 8, z - 10, x + 12, G + 12, z + 10, M('steel_dark_1'));
  b.box(x - 20, G + 12, z - 16, x + 20, G + 28, z + 20, yel);
  b.box(x - 20, G + 12, z + 20, x + 20, G + 30, z + 26, M('steel_dark_2'));
  b.box(x + 4, G + 28, z - 16, x + 18, G + 44, z - 2, yel);
  b.box(x + 5, G + 31, z - 17, x + 17, G + 42, z - 16, M('glass_tint'));
  b.box(x + 18, G + 31, z - 15, x + 19, G + 42, z - 3, M('glass_tint'));
  b.box(x - 1, G + 28, z - 18, x + 1, G + 44, z - 16, AIR);
  // Boom up and out, stick down into the pit, bucket on the floor.
  b.line(x - 6, G + 26, z - 16, x - 6, G + 56, z - 58, yel, 5);
  b.line(x - 6, G + 56, z - 58, x - 6, PIT + 10, z - 92, yel, 4);
  b.box(x - 12, PIT, z - 104, x + 1, PIT + 10, z - 90, M('steel_dark_1'));
  for (let i = 0; i < 4; i++) b.box(x - 11 + i * 3, PIT, z - 106, x - 9 + i * 3, PIT + 2, z - 104, M('steel_dark_2'));
}

function build(b: MapBuilder): MapMeta {
  b.ground(G, patches([M('dirt_3'), M('dirt_1'), M('dirt_3'), M('sand_2')], 0.018, 17), M('dirt_2'), M('rock_1'));
  // Gravel haul roads: a loop around the site and the central north-south road.
  const gravel = solid([M('stone_4')]);
  b.box(96, G - 1, 400, 320, G, 440, gravel);
  b.box(96, G - 1, 196, 320, G, 236, gravel);
  b.box(96, G - 1, 196, 112, G, 440, gravel);

  westHalf(b);

  // Perimeter hoarding (plywood panels on steel posts) around the whole site.
  const ply = planks([M('planks_3')], 0);
  b.box(0, G, 0, 3, G + 24, 320, ply);
  b.box(0, G, 0, 320, G + 24, 3, ply);
  b.box(0, G, S - 3, 320, G + 24, S, ply);
  for (let i = 20; i < 320; i += 40) {
    b.box(i, G, 3, i + 2, G + 26, 5, M('steel_dark_1'));
    b.box(i, G, S - 5, i + 2, G + 26, S - 3, M('steel_dark_1'));
    b.box(3, G, i, 5, G + 26, i + 2, M('steel_dark_1'));
    b.box(3, G, S - i - 2, 5, G + 26, S - i, M('steel_dark_1'));
  }
  b.box(0, G + 20, 0, 320, G + 22, 3, M('paint_orange'));
  b.box(0, G + 20, S - 3, 320, G + 22, S, M('paint_orange'));
  b.box(0, G + 20, 0, 3, G + 22, S, M('paint_orange'));

  b.symmetrize(SYM);

  crane(b);
  excavator(b);
  // Site gate in the north hoarding with a guard hut.
  b.clear(300, G, 0, 340, G + 24, 3);
  b.box(346, G, 8, 366, G + 24, 26, solid([M('paint_white')]));
  b.clear(348, G, 10, 364, G + 22, 24);
  b.clear(352, G + 8, 26 - 2, 360, G + 16, 26);
  b.clear(346, G, 14, 348, G + 20, 22);
  b.box(344, G + 24, 6, 368, G + 26, 28, M('steel_dark_1'));

  // Metadata ------------------------------------------------------------------------------
  const gy = G / 10;
  const meta = emptyMeta(gy, 32, 32);
  const team0: [number, number][] = [
    [2.6, 27], [2.6, 31], [2.6, 35], [2.6, 39], [4.6, 29], [4.6, 37], [3.6, 33], [5.2, 25.4],
  ];
  for (const [x, z] of team0) {
    const yaw = -Math.PI / 2;
    meta.spawns.push({ team: 0, x, y: gy, z, yaw });
    const [mx, mz] = symPoint(SYM, S / 10, S / 10, x, z);
    meta.spawns.push({ team: 1, x: mx, y: gy, z: mz, yaw: symYaw(SYM, yaw) });
  }
  const ffa: [number, number][] = [
    [3, 5], [61, 5], [3, 59], [61, 59], [9.5, 43], [54.5, 43], [9.5, 20.5], [54.5, 20.5], [16, 28.5], [48, 28.5],
    [26, 21], [38, 21], [26, 43], [38, 43], [17.5, 49.5], [46.5, 49.5], [28.5, 58.5], [35.5, 58.5], [16.5, 2.8], [47.5, 2.8],
  ];
  for (const [x, z] of ffa) meta.spawns.push({ team: -1, x, y: b.standAt(x, z, 6), z, yaw: yawToward(x, z, 32, 32) });
  meta.points.push({ id: 'A', name: 'Materials Yard', x: 17.6, y: gy, z: 32, r: 3.5 });
  meta.points.push({ id: 'B', name: 'The Pit', x: 32, y: PIT / 10, z: 30.4, r: 3.5 });
  meta.points.push({ id: 'C', name: 'Pipe Stacks', x: 46.4, y: gy, z: 32, r: 3.5 });
  meta.flags.push({ team: 0, x: 4.2, y: gy, z: 32 }, { team: 1, x: 59.8, y: gy, z: 32 });
  for (const [x, z, kind] of [
    [21, 17, 'health'], [43, 17, 'health'], [21, 47, 'health'], [43, 47, 'health'], [26.5, 36, 'armor'], [37.5, 36, 'armor'], [32, 55, 'health'],
  ] as const)
    meta.pickups.push({ kind, x, y: b.groundAt(x, z, 6), z });
  return meta;
}

export const craneYard: MapDef = {
  id: 'crane_yard',
  name: 'Crane Yard',
  description: 'A construction site with a tower crane over the pit. Knock out the mast and watch it fall.',
  size: [S, 224, S],
  modes: ['tdm', 'ffa', 'dom', 'gungame', 'sandbox', 'practice'],
  players: [4, 12],
  seed: 2024,
  version: 2,
  theme: {
    skyTop: 0x5d93d6,
    skyHorizon: 0xe6dcc8,
    fog: 0xd8d0c0,
    fogNear: 45,
    fogFar: 150,
    sun: [-0.4, 0.75, 0.45],
    sunColor: 0xffe8c8,
    sunIntensity: 1.05,
    ambient: 0xc0cce0,
    ambientIntensity: 0.55,
    outside: 0x9a8a6a,
    music: 'military',
  },
  build,
};
